import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ShieldCheck,
  Lock,
  ArrowUpRight,
  CheckCircle,
  GoogleLogo,
  FacebookLogo,
  InstagramLogo,
  Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PLATFORMS, PRICES, otherPlatform, monthlyTotal, platformLabel } from "@/lib/visibility";
import { inquiryHref, selectInterest } from "@/lib/inquiry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { facebook: FacebookLogo, instagram: InstagramLogo };

export default function VisibilityEnrollModal({ open, onClose }) {
  const [included, setIncluded] = useState("facebook");
  const [addSecond, setAddSecond] = useState(false);
  const [form, setForm] = useState({ customer_email: "", customer_name: "", business: "" });
  const [waiver, setWaiver] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const email = form.customer_email.trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const other = otherPlatform(included);
  const monthly = monthlyTotal(addSecond);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!validEmail) {
      setWaiver(false);
      return undefined;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/visibility/preview`, {
          included_platform: included,
          add_second_platform: addSecond,
          customer_email: email,
        });
        if (!cancelled) setWaiver(Boolean(data.waiver_eligible));
      } catch {
        if (!cancelled) setWaiver(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [email, validEmail, included, addSecond]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validEmail) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/visibility/enroll`, {
        included_platform: included,
        add_second_platform: addSecond,
        origin_url: window.location.origin,
        customer_email: email,
        customer_name: form.customer_name.trim() || undefined,
        business: form.business.trim() || undefined,
      });
      if (data.mode === "checkout" && data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.mode === "upgrade") {
        setDone(data);
        return;
      }
      throw new Error("Unexpected response");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start onboarding. Try again.";
      toast.error(typeof msg === "string" ? msg : "Could not start onboarding.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="visibility-enroll-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#0e0e10] border border-white/[0.1] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.85)]"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="visibility-enroll-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors z-10"
            >
              <X size={14} weight="bold" />
            </button>

            {done ? (
              <UpgradeRequested data={done} onClose={onClose} />
            ) : (
              <>
                <div className="relative px-6 sm:px-8 pt-10 pb-5">
                  <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
                  <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400">Local Visibility · onboarding</div>
                  <h3 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight text-white leading-[1.05]">
                    Choose your platforms.
                  </h3>
                  <p className="mt-3 text-zinc-400 text-[14.5px] leading-relaxed">
                    Every plan posts to your Google Business Profile plus one included social platform. We prepare
                    your photos and short clips with captions, simple branding, and light edits where needed. Add the
                    other platform for ${PRICES.addon}/month.
                  </p>
                </div>

                <form onSubmit={submit} noValidate data-testid="visibility-enroll-form" className="px-6 sm:px-8 pb-7 space-y-5">
                  <fieldset>
                    <legend className="text-[11px] tracking-[0.18em] uppercase text-zinc-400">Included social platform</legend>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      {PLATFORMS.map((p) => (
                        <PlatformOption
                          key={p.value}
                          platform={p}
                          active={included === p.value}
                          onSelect={() => setIncluded(p.value)}
                        />
                      ))}
                    </div>
                  </fieldset>

                  <label
                    data-testid="visibility-addon-toggle"
                    className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${
                      addSecond ? "border-emerald-400/40 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={addSecond}
                      onChange={(e) => setAddSecond(e.target.checked)}
                      data-testid="visibility-addon-checkbox"
                      className="sr-only"
                    />
                    <span
                      aria-hidden
                      className={`mt-0.5 flex-shrink-0 w-[18px] h-[18px] rounded-[5px] border inline-flex items-center justify-center ${
                        addSecond ? "bg-white border-white" : "border-white/25"
                      }`}
                    >
                      {addSecond && <Check size={11} weight="bold" className="text-black" />}
                    </span>
                    <span className="text-[13.5px] leading-relaxed">
                      <span className="text-white">Also publish to {other.label}</span>
                      <span className="text-zinc-400"> · +${PRICES.addon}/month</span>
                      <span className="block mt-1 text-[12.5px] text-zinc-400">
                        The same four prepared pieces, adapted — captioned, sized, scheduled, and published
                        for {other.short}. Not four extra pieces. Billed only once that account&apos;s access is ready.
                      </span>
                    </span>
                  </label>

                  <Field label="Email" testid="visibility-email" value={form.customer_email} onChange={update("customer_email")} placeholder="you@example.com" type="email" required />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your name" testid="visibility-name" value={form.customer_name} onChange={update("customer_name")} placeholder="Greg Clark" />
                    <Field label="Business" testid="visibility-business" value={form.business} onChange={update("business")} placeholder="CleanCurrent LLC" />
                  </div>

                  <Summary included={included} second={addSecond ? other.value : null} monthly={monthly} waiver={waiver} checking={checking} />

                  <p className="text-[12.5px] text-zinc-400 leading-relaxed">
                    Already completed our ${PRICES.gbpSetup} Google Business Profile setup under a different email?{" "}
                    <a
                      href={inquiryHref("local_visibility")}
                      onClick={() => { selectInterest("local_visibility"); onClose(); }}
                      data-testid="visibility-waiver-inquiry"
                      className="text-zinc-200 underline underline-offset-4 hover:text-white"
                    >
                      Send an inquiry
                    </a>{" "}
                    and we&apos;ll apply the onboarding-fee waiver manually.
                  </p>

                  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 flex items-start gap-3">
                    <ShieldCheck size={18} weight="regular" className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div className="text-[12.5px] text-zinc-400 leading-relaxed">
                      <span className="text-white text-[13.5px] block">
                        {waiver ? "No charge today" : "Secure checkout via Stripe"}
                      </span>
                      {waiver
                        ? "We'll confirm no substantial new setup work is needed, collect any missing platform access, and start your monthly plan from there."
                        : `Card details never touch our servers. You'll be redirected to Stripe to pay the $${PRICES.onboarding}.00 onboarding fee. Monthly billing starts separately once setup is complete.`}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || checking}
                    data-testid="visibility-enroll-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                  >
                    {loading
                      ? waiver ? "Sending request…" : "Redirecting to Stripe…"
                      : waiver ? "Request monthly start · $0 today" : `Pay $${PRICES.onboarding} onboarding`}
                    {!loading && <ArrowUpRight size={16} weight="bold" />}
                  </button>

                  <p className="text-center text-[11.5px] text-zinc-400 leading-relaxed">
                    By continuing, you agree to WebJesters&apos;{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-zinc-300 underline underline-offset-4 hover:text-white">Terms</a>{" "}
                    and{" "}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-zinc-300 underline underline-offset-4 hover:text-white">Privacy Policy</a>.
                    Platform additions or removals take effect the next billing month.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-[11.5px] text-zinc-400">
                    <Lock size={11} weight="regular" />
                    Powered by Stripe · 256-bit encryption
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PlatformOption({ platform, active, onSelect }) {
  const Icon = ICONS[platform.value];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      data-testid={`visibility-platform-${platform.value}`}
      className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${
        active ? "border-white bg-white text-black" : "border-white/10 bg-white/[0.02] text-zinc-200 hover:border-white/25"
      }`}
    >
      <Icon size={20} weight={active ? "fill" : "regular"} />
      <span className="text-[14px] leading-tight">
        {platform.short}
        <span className={`block text-[11.5px] mt-0.5 ${active ? "text-zinc-600" : "text-zinc-500"}`}>
          {platform.value === "instagram" ? "Professional account" : "Business Page"}
        </span>
      </span>
    </button>
  );
}

function Summary({ included, second, monthly, waiver, checking }) {
  return (
    <div data-testid="visibility-summary" className="rounded-2xl border border-white/[0.08] bg-[#0a0a0c] p-5 text-[13.5px]">
      <div className="flex items-center gap-2 text-zinc-300 flex-wrap">
        <GoogleLogo size={14} weight="bold" />
        <span>Google Business Profile</span>
        <span className="text-zinc-600">+</span>
        <span>{platformLabel(included, true)}</span>
        {second && (
          <>
            <span className="text-zinc-600">+</span>
            <span>{platformLabel(second, true)}</span>
          </>
        )}
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-3 border-t border-white/[0.06] pt-4">
        <span className="text-zinc-400">Due today · one-time onboarding</span>
        <span data-testid="visibility-due-today" className="text-white font-display text-xl tracking-tight">
          {checking ? "…" : waiver ? "$0" : `$${PRICES.onboarding}`}
        </span>
      </div>
      {waiver && !checking && (
        <div data-testid="visibility-waiver-note" className="mt-1 text-[12.5px] text-emerald-300">
          Onboarding fee waived — we found your completed ${PRICES.gbpSetup} Google Business Profile setup.
        </div>
      )}
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <span className="text-zinc-400">Monthly · starts after setup</span>
        <span data-testid="visibility-monthly-total" className="text-white font-display text-xl tracking-tight">
          ${monthly}<span className="text-zinc-400 text-[13px] font-sans">/mo</span>
        </span>
      </div>
      <p className="mt-3 text-[12px] text-zinc-500 leading-relaxed">
        ${PRICES.monthly} base{second ? ` + $${PRICES.addon} second platform` : ""}. Month-to-month, cancel anytime.
        Billing begins once setup is complete and we have account access and content to post.
      </p>
    </div>
  );
}

function UpgradeRequested({ data, onClose }) {
  return (
    <div data-testid="visibility-upgrade-success" className="px-6 sm:px-8 pt-12 pb-8">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
        <CheckCircle size={26} weight="regular" />
      </div>
      <h3 className="mt-6 font-display text-3xl sm:text-4xl tracking-tight text-white leading-[1.05]">
        Request received.
        <br />
        No charge today.
      </h3>
      <p className="mt-4 text-zinc-400 text-[14.5px] leading-relaxed">
        Your ${PRICES.onboarding} onboarding fee is waived because you already completed our ${PRICES.gbpSetup} Google
        Business Profile setup. Greg will email you within one business day to confirm platform access and start your
        monthly plan — <span className="text-white">${Math.round(data.monthly_cents / 100)}/month</span> for Google
        Business Profile + {platformLabel(data.included_platform, true)}
        {data.second_platform ? ` + ${platformLabel(data.second_platform, true)}` : ""}.
      </p>
      <button
        type="button"
        onClick={onClose}
        data-testid="visibility-upgrade-done"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-[13.5px] font-medium hover:bg-zinc-200 transition-colors"
      >
        Done
      </button>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <div>
      <label htmlFor={testid} className="text-[11px] tracking-[0.18em] uppercase text-zinc-400">
        {label}
        {required && <span className="text-brand-400 ml-1">*</span>}
      </label>
      <input
        id={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-[#0a0a0c] border border-white/10 rounded-xl px-4 py-3 text-[14.5px] text-white placeholder-zinc-600 focus:outline-none focus:border-white/30 transition-colors"
      />
    </div>
  );
}
